import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { sectionId } from '../utils/routes';

const FAQItem = ({ question, answer, isOpen, onClick, index }) => {
  return (
    <motion.div
      className="border-b border-white/10 last:border-b-0"
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.05 }}
    >
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left group"
        data-testid={`faq-item-${index}`}
      >
        <span className="text-white text-lg sm:text-xl font-light pr-8 group-hover:text-[#FFD700] transition-colors duration-300">
          {question}
        </span>
        <motion.span
          className="flex-shrink-0 w-8 h-8 border border-[#FFD700]/50 flex items-center justify-center text-[#FFD700] group-hover:bg-[#FFD700] group-hover:text-black transition-all duration-300"
          animate={{ rotate: isOpen ? 45 : 0 }}
          transition={{ duration: 0.2 }}
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        </motion.span>
      </button>
      
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <p className="text-white/90 text-base font-light pb-6 pr-12 leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQSection = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language?.substring(0, 2) || 'it';
  const [openIndex, setOpenIndex] = useState(null);

  const faqItems = t('faq.items', { returnObjects: true });

  const toggleItem = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id={sectionId(lang, 'faq')} className="py-32 bg-[#0a0a0a] relative overflow-hidden">
      {/* Top border */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#FFD700]/30 to-transparent" />
      
      {/* Decorative question mark */}
      <div className="absolute top-20 right-10 text-[20vw] font-serif text-white/[0.02] leading-none pointer-events-none select-none">
        ?
      </div>

      <div className="max-w-4xl mx-auto px-6 lg:px-12">
        {/* Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <span className="text-[#FFD700] text-xs tracking-[0.4em] uppercase mb-6 block">
            FAQ
          </span>
          <h2 className="font-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-white leading-[0.9] mb-6">
            <span className="block sm:inline">{t('faq.title')}</span>
            <span className="text-outline block sm:inline sm:ml-4">{t('faq.title_outline')}</span>
          </h2>
          <p className="text-white/80 text-lg font-light max-w-xl mx-auto">
            {t('faq.subtitle')}
          </p>
        </motion.div>

        {/* FAQ Items */}
        <div className="border-t border-white/10">
          {Array.isArray(faqItems) && faqItems.map((item, index) => (
            <FAQItem
              key={item.question}
              question={item.question}
              answer={item.answer}
              isOpen={openIndex === index}
              onClick={() => toggleItem(index)}
              index={index}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default FAQSection;
